// V2.1A Step D.1 — Pannello Documenti SAL
// Genera PDF tecnico (blob), Upload PDF firmato versionato con SHA256,
// Allegati tecnici (upload/lista/download). NB: nessun endpoint delete → out of scope.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  salApi,
  extractApiError,
  triggerBlobDownload,
  filenameFromContentDisposition,
} from './salApi';

const TECH_DOC_TYPES = [
  { value: 'libretto_misure', label: 'Libretto misure' },
  { value: 'fotografie', label: 'Fotografie' },
  { value: 'verbale', label: 'Verbale' },
  { value: 'richiesta_sal', label: 'Richiesta SAL' },
  { value: 'autorizzazione_fatturazione', label: 'Autorizzazione fatturazione' },
  { value: 'comunicazione', label: 'Comunicazione' },
  { value: 'altro', label: 'Altro' },
];

const SIGNED_UPLOAD_STATUSES = new Set(['approvato', 'autorizzato_fattura']);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT'); } catch { return iso; }
};

const fmtSize = (bytes) => {
  const n = Number(bytes || 0);
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const shortHash = (h) => (h && h.length >= 12 ? `${h.slice(0, 8)}…${h.slice(-4)}` : h || '—');

const SubcontractSALDocumentsPanel = ({ sal, onChanged }) => {
  const [documents, setDocuments] = useState(sal?.documents || []);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);

  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const [signedFile, setSignedFile] = useState(null);
  const [uploadingSigned, setUploadingSigned] = useState(false);
  const [signedError, setSignedError] = useState(null);
  const signedInputRef = useRef(null);

  const [techFile, setTechFile] = useState(null);
  const [techType, setTechType] = useState('libretto_misure');
  const [techNotes, setTechNotes] = useState('');
  const [uploadingTech, setUploadingTech] = useState(false);
  const [techError, setTechError] = useState(null);
  const techInputRef = useRef(null);

  const [downloadingId, setDownloadingId] = useState(null);

  const canUploadSigned = SIGNED_UPLOAD_STATUSES.has(sal?.status);
  const isReadonlyStatus = sal?.status === 'annullato';

  // Carica documenti dall'API (sorgente di verità dopo ogni mutazione).
  const reloadDocuments = useCallback(async () => {
    if (!sal?.id) return;
    setLoadingList(true);
    setListError(null);
    try {
      const r = await salApi.listDocuments(sal.id);
      setDocuments(r.data?.items || []);
    } catch (e) {
      setListError(extractApiError(e).message);
    } finally {
      setLoadingList(false);
    }
  }, [sal?.id]);

  useEffect(() => {
    // Al mount / cambio SAL: reload autoritativo (sal.documents può essere stale)
    reloadDocuments();
  }, [reloadDocuments]);

  // Separazione documenti firmati / tecnici
  const { signedActive, signedHistory, technicalDocs } = useMemo(() => {
    const signed = documents.filter((d) => d.document_type === 'sal_pdf_firmato');
    const active = signed.find((d) => d.is_active) || null;
    const history = signed
      .filter((d) => !d.is_active)
      .sort((a, b) => (b.version || 0) - (a.version || 0));
    const tech = documents
      .filter((d) => d.document_type !== 'sal_pdf_firmato')
      .sort((a, b) => (b.uploaded_at || '').localeCompare(a.uploaded_at || ''));
    return { signedActive: active, signedHistory: history, technicalDocs: tech };
  }, [documents]);

  const handleGeneratePdf = async () => {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    setPdfError(null);
    try {
      const r = await salApi.generatePdf(sal.id);
      const cd = r.headers?.['content-disposition'] || r.headers?.['Content-Disposition'];
      const fname = filenameFromContentDisposition(cd, `SAL_${sal.sal_number}.pdf`);
      triggerBlobDownload(r.data, fname);
      toast.success('PDF SAL generato');
    } catch (e) {
      // Se la risposta è un blob JSON di errore, prova a leggerlo
      let msg = extractApiError(e).message;
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          msg = j?.detail?.message || j?.detail || msg;
        } catch { /* ignore */ }
      }
      setPdfError(msg);
      toast.error(msg);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleUploadSigned = async () => {
    if (!signedFile || uploadingSigned) return;
    setUploadingSigned(true);
    setSignedError(null);
    try {
      await salApi.uploadSignedPdf(sal.id, signedFile);
      toast.success('PDF firmato caricato');
      setSignedFile(null);
      if (signedInputRef.current) signedInputRef.current.value = '';
      await reloadDocuments();
      if (onChanged) onChanged();
    } catch (e) {
      const msg = extractApiError(e).message;
      setSignedError(msg);
      toast.error(msg);
    } finally {
      setUploadingSigned(false);
    }
  };

  const handleUploadTech = async () => {
    if (!techFile || uploadingTech) return;
    setUploadingTech(true);
    setTechError(null);
    try {
      await salApi.uploadDocument(sal.id, techFile, techType, techNotes || null);
      toast.success('Allegato caricato');
      setTechFile(null);
      setTechNotes('');
      if (techInputRef.current) techInputRef.current.value = '';
      await reloadDocuments();
      if (onChanged) onChanged();
    } catch (e) {
      const msg = extractApiError(e).message;
      setTechError(msg);
      toast.error(msg);
    } finally {
      setUploadingTech(false);
    }
  };

  const handleDownload = async (doc) => {
    if (downloadingId) return;
    setDownloadingId(doc.id);
    try {
      const r = await salApi.downloadDocument(sal.id, doc.id);
      const cd = r.headers?.['content-disposition'] || r.headers?.['Content-Disposition'];
      const fname = filenameFromContentDisposition(cd, doc.filename || 'documento');
      triggerBlobDownload(r.data, fname);
    } catch (e) {
      toast.error(extractApiError(e).message);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="sal-documents-panel">
      {/* SEZIONE 1 · Genera PDF tecnico */}
      <section
        className="border border-neutral-200 rounded-md p-4 bg-white"
        data-testid="sal-doc-section-generate"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">PDF tecnico SAL</h3>
            <p className="text-xs text-neutral-600 mt-1">
              Genera on-demand il PDF del SAL con lo stato corrente. Nessun record persistente.
            </p>
          </div>
          <Button
            onClick={handleGeneratePdf}
            disabled={generatingPdf}
            data-testid="sal-doc-generate-pdf-btn"
          >
            {generatingPdf ? 'Generazione…' : 'Genera PDF'}
          </Button>
        </div>
        {pdfError && (
          <div className="mt-3 flex items-center justify-between text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1">
            <span data-testid="sal-doc-generate-error">{pdfError}</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2"
              onClick={handleGeneratePdf}
              disabled={generatingPdf}
              data-testid="sal-doc-generate-retry"
            >
              Riprova
            </Button>
          </div>
        )}
      </section>

      {/* SEZIONE 2 · PDF firmato (versioning) */}
      <section
        className="border border-neutral-200 rounded-md p-4 bg-white"
        data-testid="sal-doc-section-signed"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">PDF firmato</h3>
            <p className="text-xs text-neutral-600 mt-1">
              Carica il PDF firmato dal fornitore. Ogni upload crea una nuova versione;
              solo l&apos;ultima è marcata come attiva.
            </p>
          </div>
          {!canUploadSigned && (
            <Badge className="bg-neutral-100 text-neutral-600 border border-neutral-300">
              Disponibile solo per SAL approvato o autorizzato_fattura
            </Badge>
          )}
        </div>

        {canUploadSigned && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs">File PDF</Label>
              <Input
                ref={signedInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setSignedFile(e.target.files?.[0] || null)}
                disabled={uploadingSigned}
                data-testid="sal-doc-signed-file-input"
              />
            </div>
            <Button
              onClick={handleUploadSigned}
              disabled={!signedFile || uploadingSigned}
              data-testid="sal-doc-signed-upload-btn"
            >
              {uploadingSigned ? 'Upload…' : 'Carica firmato'}
            </Button>
          </div>
        )}
        {signedError && (
          <div className="mt-2 text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1" data-testid="sal-doc-signed-error">
            {signedError}
          </div>
        )}

        {/* Versione attiva */}
        <div className="mt-4">
          <div className="text-xs uppercase text-neutral-500 mb-1">Versione attiva</div>
          {signedActive ? (
            <SignedRow
              doc={signedActive}
              active
              onDownload={() => handleDownload(signedActive)}
              downloading={downloadingId === signedActive.id}
            />
          ) : (
            <div className="text-xs text-neutral-500 italic" data-testid="sal-doc-signed-active-empty">
              Nessuna versione firmata caricata.
            </div>
          )}
        </div>

        {/* Storico versioni */}
        {signedHistory.length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase text-neutral-500 mb-1">
              Storico versioni ({signedHistory.length})
            </div>
            <div className="space-y-1" data-testid="sal-doc-signed-history">
              {signedHistory.map((d) => (
                <SignedRow
                  key={d.id}
                  doc={d}
                  onDownload={() => handleDownload(d)}
                  downloading={downloadingId === d.id}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* SEZIONE 3 · Allegati tecnici */}
      <section
        className="border border-neutral-200 rounded-md p-4 bg-white"
        data-testid="sal-doc-section-technical"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">Allegati tecnici</h3>
            <p className="text-xs text-neutral-600 mt-1">
              Libretti misure, fotografie, verbali, comunicazioni. La cancellazione non è ancora
              disponibile lato backend.
            </p>
          </div>
        </div>

        {!isReadonlyStatus && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs">Tipo documento</Label>
              <Select value={techType} onValueChange={setTechType} disabled={uploadingTech}>
                <SelectTrigger data-testid="sal-doc-tech-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TECH_DOC_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`sal-doc-tech-type-${t.value}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">File</Label>
              <Input
                ref={techInputRef}
                type="file"
                onChange={(e) => setTechFile(e.target.files?.[0] || null)}
                disabled={uploadingTech}
                data-testid="sal-doc-tech-file-input"
              />
            </div>
            <div>
              <Button
                onClick={handleUploadTech}
                disabled={!techFile || uploadingTech}
                data-testid="sal-doc-tech-upload-btn"
                className="w-full"
              >
                {uploadingTech ? 'Upload…' : 'Carica allegato'}
              </Button>
            </div>
            <div className="md:col-span-4">
              <Label className="text-xs">Note (opzionale)</Label>
              <Textarea
                rows={2}
                value={techNotes}
                onChange={(e) => setTechNotes(e.target.value)}
                disabled={uploadingTech}
                data-testid="sal-doc-tech-notes"
              />
            </div>
          </div>
        )}
        {techError && (
          <div className="mt-2 text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1" data-testid="sal-doc-tech-error">
            {techError}
          </div>
        )}

        <div className="mt-4">
          {loadingList && (
            <div className="text-xs text-neutral-500 py-2" data-testid="sal-doc-list-loading">
              Caricamento…
            </div>
          )}
          {listError && (
            <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1" data-testid="sal-doc-list-error">
              {listError}
            </div>
          )}
          {!loadingList && !listError && technicalDocs.length === 0 && (
            <div className="text-xs text-neutral-500 italic" data-testid="sal-doc-tech-empty">
              Nessun allegato caricato.
            </div>
          )}
          {technicalDocs.length > 0 && (
            <div className="border border-neutral-200 rounded overflow-x-auto" data-testid="sal-doc-tech-list">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-xs uppercase text-neutral-600">
                  <tr>
                    <th className="text-left px-3 py-2">Tipo</th>
                    <th className="text-left px-3 py-2">Nome file</th>
                    <th className="text-right px-3 py-2">Dim.</th>
                    <th className="text-left px-3 py-2">SHA256</th>
                    <th className="text-left px-3 py-2">Caricato</th>
                    <th className="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {technicalDocs.map((d) => (
                    <tr key={d.id} className="border-t border-neutral-100" data-testid={`sal-doc-tech-row-${d.id}`}>
                      <td className="px-3 py-2">
                        <Badge className="bg-slate-100 text-slate-700 border border-slate-300">
                          {(TECH_DOC_TYPES.find((t) => t.value === d.document_type)?.label) || d.document_type}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 max-w-[240px] truncate" title={d.filename}>{d.filename}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtSize(d.size_bytes)}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-neutral-600" title={d.sha256}>
                        {shortHash(d.sha256)}
                      </td>
                      <td className="px-3 py-2 text-xs text-neutral-600">{fmtDate(d.uploaded_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(d)}
                          disabled={downloadingId === d.id}
                          data-testid={`sal-doc-tech-download-${d.id}`}
                        >
                          {downloadingId === d.id ? '…' : 'Scarica'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const SignedRow = ({ doc, active = false, onDownload, downloading }) => (
  <div
    className={`flex items-center gap-3 border rounded px-3 py-2 ${active ? 'border-emerald-300 bg-emerald-50/40' : 'border-neutral-200 bg-white'}`}
    data-testid={active ? 'sal-doc-signed-active-row' : `sal-doc-signed-history-row-${doc.id}`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium truncate" title={doc.filename}>{doc.filename}</span>
        <Badge
          className={active
            ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
            : 'bg-neutral-100 text-neutral-600 border border-neutral-300'}
          data-testid={active ? 'sal-doc-signed-active-badge' : `sal-doc-signed-history-badge-${doc.version}`}
        >
          v{doc.version} {active ? '· ATTIVA' : ''}
        </Badge>
      </div>
      <div className="text-[11px] text-neutral-600 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
        <span>{fmtDate(doc.uploaded_at)}</span>
        <span>· autore: {doc.uploaded_by}</span>
        <span>· {fmtSize(doc.size_bytes)}</span>
        <span className="font-mono" title={doc.sha256}>· sha256 {shortHash(doc.sha256)}</span>
      </div>
    </div>
    <Button
      size="sm"
      variant="outline"
      onClick={onDownload}
      disabled={downloading}
      data-testid={active ? 'sal-doc-signed-active-download' : `sal-doc-signed-history-download-${doc.id}`}
    >
      {downloading ? '…' : 'Scarica'}
    </Button>
  </div>
);

export default SubcontractSALDocumentsPanel;
